// Vanilla CSS for NotificationProvider toasts. Prefix: ntf-. Injected locally.
// Playful Frost glass, tuned for the teen LEARNER app: frosted popover surface
// (--glass-popover-*) + soft top sheen, a friendly colored ICON CHIP, a springy
// pop-in, and a soft per-type color glow. Type identity comes entirely from the
// semantic status tokens (--color-{success,warning,error,info}{,-bg,-border,-text}).
// Auto-dismiss progress is a rounded pill that PAUSES while hovered (JS pauses
// the dismiss timer; CSS pauses the bar) so learners can finish reading.
export const notificationProviderStyles = `
  .ntf-container {
    position: fixed;
    bottom: var(--space-4);
    right: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    z-index: var(--z-toast);
    pointer-events: none;
  }
  .ntf-item { pointer-events: auto; }

  /* Animation layer — carries the pop-in/out transform and is promoted to its
     own compositor layer (will-change). Deliberately has NO backdrop-filter:
     the blurred .ntf-toast surface inside stays static, gets composited once,
     and this layer is just transformed on the GPU — no per-frame re-blur. */
  .ntf-shell { will-change: transform, opacity; }

  .ntf-toast {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    padding: var(--space-4);
    padding-bottom: var(--space-5);
    min-width: 320px;
    max-width: 420px;
    overflow: hidden;
    font-family: var(--font-sans);
    border-radius: var(--radius-lg);
    background: var(--glass-popover-bg-strong);
    -webkit-backdrop-filter: var(--glass-popover-filter);
    backdrop-filter: var(--glass-popover-filter);
    border: 1px solid var(--glass-border);
    box-shadow: var(--glass-popover-shadow);
  }
  /* frosted top-edge sheen — matches the glass Card / popover language */
  .ntf-toast::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 48%;
    background: var(--glass-popover-sheen);
    pointer-events: none;
    z-index: 0;
  }
  .ntf-toast > * { position: relative; z-index: 1; }

  /* friendly colored icon chip — the toast's "face" */
  .ntf-chip {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
    border: 1px solid transparent;
  }
  .ntf-body { flex: 1 1 0%; min-width: 0; padding-top: 1px; }
  .ntf-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: var(--fs-md);
    margin-bottom: var(--space-1);
  }
  .ntf-msg {
    font-size: var(--fs-base);
    line-height: 1.4;
    word-break: break-word;
    color: var(--text-primary);
  }
  .ntf-dismiss {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-1);
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-secondary);
    border-radius: var(--radius-xs);
    transition: background-color var(--duration-fast) var(--ease-standard),
      color var(--duration-fast) var(--ease-standard);
  }
  .ntf-dismiss:hover {
    background-color: var(--glass-active-bg);
    color: var(--text-primary);
  }

  /* auto-dismiss progress — a rounded pill inset from the edges */
  .ntf-prog-track {
    position: absolute;
    bottom: var(--space-2);
    left: var(--space-4);
    right: var(--space-4);
    height: 4px;
    border-radius: var(--radius-full);
    background-color: rgba(15, 72, 89, 0.08);
    overflow: hidden;
  }
  .ntf-prog-fill { height: 100%; border-radius: var(--radius-full); }
  /* pause the countdown while the learner is reading (hovering) */
  .ntf-toast:hover .ntf-prog-fill { animation-play-state: paused; }

  /* springy entrance / exit — in the spirit of the app's emojiPopIn / bounceIn */
  @keyframes ntfPopIn {
    0%   { opacity: 0; transform: translateX(28px) scale(0.92); }
    55%  { opacity: 1; transform: translateX(-4px) scale(1.02); }
    100% { opacity: 1; transform: translateX(0) scale(1); }
  }
  @keyframes ntfPopOut {
    0%   { opacity: 1; transform: translateX(0) scale(1); }
    100% { opacity: 0; transform: translateX(28px) scale(0.9); }
  }
  .ntf-anim-in { animation: ntfPopIn 360ms var(--ease-glass) forwards; }
  .ntf-anim-out { animation: ntfPopOut 200ms var(--ease-standard) forwards; }

  /* per-type identity — icon chip (tinted fill + ring + colored glyph) */
  .ntf-chip-success { background: var(--color-success-bg); border-color: var(--color-success-border); color: var(--color-success); }
  .ntf-chip-error   { background: var(--color-error-bg);   border-color: var(--color-error-border);   color: var(--color-error); }
  .ntf-chip-warning { background: var(--color-warning-bg); border-color: var(--color-warning-border); color: var(--color-warning); }
  .ntf-chip-info    { background: var(--color-info-bg);    border-color: var(--color-info-border);    color: var(--color-info); }
  /* per-type identity — eyebrow label */
  .ntf-label-success { color: var(--color-success-text); }
  .ntf-label-error   { color: var(--color-error-text); }
  .ntf-label-warning { color: var(--color-warning-text); }
  .ntf-label-info    { color: var(--color-info-text); }
  /* per-type identity — auto-dismiss progress fill */
  .ntf-prog-success { background-color: var(--color-success); }
  .ntf-prog-error   { background-color: var(--color-error); }
  .ntf-prog-warning { background-color: var(--color-warning); }
  .ntf-prog-info    { background-color: var(--color-info); }
  /* per-type identity — soft color glow layered on the glass shadow */
  .ntf-success { box-shadow: var(--glass-popover-shadow), 0 8px 28px rgba(74, 175, 124, 0.28); }
  .ntf-error   { box-shadow: var(--glass-popover-shadow), 0 8px 28px rgba(226, 91, 91, 0.26); }
  .ntf-warning { box-shadow: var(--glass-popover-shadow), 0 8px 28px rgba(230, 160, 35, 0.26); }
  .ntf-info    { box-shadow: var(--glass-popover-shadow), 0 8px 28px rgba(87, 194, 211, 0.26); }
`;
